import React from 'react';
import PropTypes from 'prop-types';

import styles from './styles/styles.module.scss';
import formStyles from '../../styles/styles.module.scss';
import baseStyles from '../../../../styles/baseStyles.module.scss';
import { ProPageStrings } from '../../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { validatePaymentForm, getTotalCost, getTotalBaseAnnualCost } from '../../../../utils/form_validaters/payment_form_helper';
import { hasErrors } from '../../../../utils/form_validaters/user_form_helper';
import { ServerCommunicationMixin } from '../../../../application/javascripts/react_components/mixins/shared/server_communication_mixin';
import { LanguageSupportMixin } from '../../../../application/javascripts/react_components/mixins/shared/language_support_mixin';

import PlanTypeCard from '../PlanTypeCard';
import IconTabBar from '../../../shared/IconTabBar';

import Wrapper from '../../Wrapper';
import CreditCardForm from '../../CreditCardForm/CreditCardForm';
import ExternalPayForm from '../../ExternalPayForm/ExternalPayForm';
import CouponCode from '../../CouponCode';

import FormButton from '../../FormButton';

class PaymentForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      errors: {
        name: '',
        number: '',
        cvv: '',
        'cc-exp-month': '',
        'cc-exp-year': '',
        postalCode: ''
      },
      name: '',
      number: '',
      cvv: '',
      'cc-exp-month': null,
      'cc-exp-year': null,
      postalCode: '',
      couponError: '',
      serverError: null,
      maxCodeSize: 3,
      isCouponInputExpanded: false,
      submitting: false,
      paypalToken: null,
      paypal: null,
      applePay: null,
      amazonPayToken: null,
      applePayToken: null,
      activePaymentTab: 0
    };
  }

  static TEXT = { ...ProPageStrings.paymentText, creditCardForm: ProPageStrings.creditCardForm };
  static SUCCESS_URL = '/pro/welcome';
  static ANNUAL_PLAN = 0;
  static THREE_YEAR_PLAN = 1;
  static GIFT_CARD = 'GiftCard';

  componentDidMount() {
    if (this.props.paymentTypes[1].showTab && typeof recurly !== 'undefined') {
      recurly.configure(this.props.recurlyPublicKey);
      this.setupThirdPartyPayments();
    }
  }

  // setups
  setupThirdPartyPayments() {
    this.setupApplePay();

    const paypal = recurly.PayPal({
      display: {
        displayName: 'AllTrails Pro',
        amount: this.props.planTypes[this.props.activePlanType].priceTotal
      }
    });
    paypal.on('error', err => {
      console.error(err);
    });
    paypal.on('token', token => {
      this.setState({ paypalToken: token.id }, this.submitPayment);
    });

    this.setState({ paypal });
  }

  setupApplePay() {
    let applePay;
    if (this.props.paymentTypes[2].showTab) {
      let priceTotal = this.props.planTypes[this.props.activePlanType].priceTotal;
      if (this.props.verifiedCoupon && this.props.verifiedCoupon.discountedPricePerYear) {
        priceTotal = this.props.verifiedCoupon.discountedPricePerYear;
      }
      try {
        applePay = recurly.ApplePay({
          country: 'US', // this is the merchant's country code, so will remain US
          currency: this.props.currencyCode,
          label: 'AllTrails Pro',
          total: priceTotal
        });
        applePay.on('error', err => {
          console.error('Apple Pay error', err);
        });
        applePay.on('token', token => {
          // Submit the token to server when flow is completed successfully
          this.setState({ applePayToken: token.id }, this.submitPayment);
        });
      } catch (error) {
        console.error(error);
      }
    }

    this.setState({ applePay });
  }

  // handlers
  handlePaymentTabSwitch = activePaymentTab => {
    this.setState({ activePaymentTab });
  };

  handleCreditCardFormChange = event => {
    const { name, value } = event.target;
    const monthAndYear = this.getMonthAndYear();
    const payload = validatePaymentForm(name, value, monthAndYear, this.state.errors, PaymentForm.TEXT.creditCardForm, this.state.maxCodeSize);
    this.setState({ ...payload });
  };

  handleApplePayClick = event => {
    if (!this.state.applePay) {
      return;
    }

    this.state.applePay.begin();
  };

  handlePaypalClick = event => {
    if (!this.state.paypal) {
      return;
    }

    this.state.paypal.start();
  };

  handleAmazonClick = amazonPayToken => {
    this.setState({ amazonPayToken }, this.submitPayment);
  };

  handlePaymentFormSuccess = data => {
    ServerCommunicationMixin.handleGaqPush(['_trackEvent', 'pro_step_2', 'submit']);
    window.location.href = LanguageSupportMixin.wrapUrlSafe(PaymentForm.SUCCESS_URL, this.props.locale);
  };

  handlePaymentFormError = err => {
    ServerCommunicationMixin.handleGaqPush(['_trackEvent', 'pro_step_2', 'payment_error']);
    let { errors, serverError } = this.state;

    let errorObj;
    try {
      errorObj = JSON.parse(err.responseText);
    } catch (e) {
      errorObj = false;
    }

    if (errorObj) {
      if (errorObj.base) {
        serverError = errorObj.base[0];
      }
      if (errorObj.cc_number) {
        errors.number = errorObj.cc_number[0];
      }
      if (errorObj.cc_expiration_month) {
        errors['cc-exp-month'] = errorObj.cc_expiration_month[0];
      }
      if (errorObj.cc_expiration_year) {
        errors['cc-exp-year'] = errorObj.cc_expiration_year[0];
      }
      if (errorObj.cc_csc) {
        errors.cvv = errorObj.cc_csc[0];
      }
    } else {
      serverError = PaymentForm.TEXT.serverError;
    }

    this.setState({ errors, serverError, submitting: false });
  };

  // gets
  getMonthAndYear() {
    return {
      'cc-exp-month': this.state['cc-exp-month'],
      'cc-exp-year': this.state['cc-exp-year']
    };
  }

  getCouponCodeInput() {
    return (
      <CouponCode
        text={PaymentForm.TEXT}
        expanded={this.props.isCouponInputExpanded}
        removeHandler={this.props.handleRemoveCoupon}
        showHandler={this.props.handleShowCouponCodeInput}
        changeHandler={this.props.handleCouponCodeChange}
        applyHandler={this.props.handleApplycoupon}
        isSuccess={this.props.isCouponApplied}
        isError={this.props.couponError}
        errorMessage={this.props.couponError}
        value={this.props.couponCode}
      />
    );
  }

  clearErrors() {
    this.setState({
      errors: {
        name: '',
        number: '',
        cvv: '',
        'cc-exp-month': '',
        'cc-exp-year': '',
        postalCode: ''
      }
    });
  }

  getFootText() {
    const currentPlan = this.props.planTypes[this.props.activePlanType];
    const totalCost = getTotalCost(this.props.planTypes, this.props.activePlanType, this.props.verifiedCoupon, this.props.currencyCode);
    const baseAnnualCost = getTotalBaseAnnualCost(this.props.planTypes, this.props.activePlanType, this.props.currencyCode);
    const months = currentPlan.numberOfMonths;
    const { footText } = PaymentForm.TEXT;

    if (this.props.activePlanType === PaymentForm.THREE_YEAR_PLAN) {
      return PaymentForm.TEXT.threeYearFootText;
    }

    return this.props.isCouponApplied
      ? `${footText.lead} ${totalCost} ${footText.mid} ${months} ${footText.discountMid} ${baseAnnualCost} ${footText.discountTail}`
      : `${footText.lead} ${totalCost} ${footText.mid} ${months} ${footText.tail}`;
  }

  getFirstAndLastName() {
    const fullName = this.state.name.split(' ');
    const billing_first_name = fullName[0];
    const billing_last_name = fullName[fullName.length - 1];

    return { billing_first_name, billing_last_name };
  }

  // validators

  noGiftCard() {
    if (!this.props.verifiedCoupon) {
      return true;
    }

    return this.props.verifiedCoupon.type !== PaymentForm.GIFT_CARD;
  }

  noAlternatePayment() {
    return this.state.applePayToken === null && this.state.amazonPayToken === null && this.state.paypalToken === null;
  }

  failedFormValidation() {
    let validation;
    let validationErrors = { ...this.state.errors };
    const monthAndYear = this.getMonthAndYear();

    for (let name in this.state.errors) {
      validation = validatePaymentForm(
        name,
        this.state[name],
        monthAndYear,
        this.state.errors,
        PaymentForm.TEXT.creditCardForm,
        this.state.maxCodeSize
      );
      validationErrors = { ...validationErrors, ...validation.errors };
    }
    this.setState({ errors: validationErrors });
    return hasErrors(validationErrors);
  }

  submitPayment = e => {
    if (e) {
      e.preventDefault();
    }

    if (this.state.submitting) {
      return;
    }

    this.setState({ submitting: true }, () => ServerCommunicationMixin.handleGaqPush(['_trackEvent', 'pro_step_2', 'submit']));
    if (this.noAlternatePayment() && this.noGiftCard()) {
      if (this.failedFormValidation()) {
        this.setState({ submitting: false });
        return;
      }
    }

    this.clearErrors();
    const { billing_first_name, billing_last_name } = this.getFirstAndLastName();
    const submissionData = {
      plan: this.props.activePlanType,
      paypal_token: this.state.paypalToken,
      apple_pay_token: this.state.applePayToken,
      amazon_pay_token: this.state.amazonPayToken,
      user: {
        user_entered_coupon_code: this.props.verifiedCoupon ? this.props.verifiedCoupon.code : null,
        cc_number: this.state.number,
        cc_expiration_month: this.state['cc-exp-month'] ? this.state['cc-exp-month'].value : null,
        cc_expiration_year: this.state['cc-exp-year'] ? this.state['cc-exp-year'].value : null,
        cc_csc: this.state.cvv,
        zip: this.state.postalCode,
        billing_first_name,
        billing_last_name
      }
    };

    ServerCommunicationMixin.putApiEndpoint('/pro', submissionData, this.handlePaymentFormSuccess, this.handlePaymentFormError, () => {});
  };

  // renders
  renderServerError() {
    return <div className={this.state.serverError ? styles.serverError : ''}>{this.state.serverError}</div>;
  }

  renderExternalPayForm() {
    return (
      <div>
        <div>
          <div className={formStyles.promptHead}>{PaymentForm.TEXT.chooseMethodHead}</div>
          <div className={formStyles.prompt}>{PaymentForm.TEXT.chooseMethodPrompt}</div>
        </div>
        {this.props.isCouponInputShown ? this.getCouponCodeInput() : ''}
        <ExternalPayForm
          text={PaymentForm.TEXT}
          paypalHandler={this.handlePaypalClick}
          applePayHandler={this.handleApplePayClick}
          amazonPayHandler={this.handleAmazonClick}
          showApplePay={this.props.paymentTypes[2].showTab}
          showAmazonPay={this.props.paymentTypes[3].showTab}
          amazonSellerId={this.props.amazonSellerId}
          amazonClientId={this.props.amazonClientId}
        />
        {this.renderServerError()}
      </div>
    );
  }

  renderCreditCardForm() {
    return (
      <form onSubmit={this.submitPayment}>
        <CreditCardForm
          text={PaymentForm.TEXT.creditCardForm}
          errors={this.state.errors}
          changeHandler={this.handleCreditCardFormChange}
          name={this.state.name}
          number={this.state.number}
          month={this.state['cc-exp-month']}
          year={this.state['cc-exp-year']}
          cvv={this.state.cvv}
          postalCode={this.state.postalCode}
          submitHandler={this.submitPayment}
        />
        {this.props.isCouponInputShown ? this.getCouponCodeInput() : ''}
        {this.renderServerError()}
        <div className={styles.submit}>
          <FormButton value={PaymentForm.TEXT.submitText} disabled={this.state.submitting} />
        </div>
      </form>
    );
  }

  renderPaymentFormBody() {
    return this.state.activePaymentTab === 0 ? this.renderCreditCardForm() : this.renderExternalPayForm();
  }

  getPlanTypeCard() {
    return (
      <PlanTypeCard
        option={this.props.selectedPlan}
        currencyCode={this.props.currencyCode}
        verifiedCoupon={this.props.verifiedCoupon}
        isCouponApplied={this.props.isCouponApplied}
        staticCard={true}
      />
    );
  }

  render() {
    const footText = this.getFootText();

    return (
      <Wrapper text={PaymentForm.TEXT} foot={footText}>
        <div className={baseStyles.section}>
          <div className={baseStyles.row}>
            {this.getPlanTypeCard()}
            <div className={`${styles.prompt} ${baseStyles.link} `} onClick={this.props.goBack}>
              {PaymentForm.TEXT.changePlanText}
            </div>
          </div>
          <div className={baseStyles.row}>
            <IconTabBar
              text={PaymentForm.TEXT}
              tabs={this.props.paymentTypes}
              handler={this.handlePaymentTabSwitch}
              activeTab={this.state.activePaymentTab}
            />
          </div>
          <div className={`${formStyles.body} ${styles.body}`}>{this.renderPaymentFormBody()}</div>
        </div>
      </Wrapper>
    );
  }
}

PaymentForm.defaultProps = {
  changePlanText: 'Change plan'
};

PaymentForm.propTypes = {
  activePlanType: PropTypes.number,
  amazonClientId: PropTypes.string,
  changePlanText: PropTypes.string,
  couponError: PropTypes.string,
  currencyCode: PropTypes.string,
  goBack: PropTypes.func,
  handleApplyCoupon: PropTypes.func,
  handleCouponCodeChange: PropTypes.func,
  handleRemoveCoupon: PropTypes.func,
  handleShowCouponCodeInput: PropTypes.func,
  isCouponApplied: PropTypes.bool,
  isCouponInputExpanded: PropTypes.bool,
  isCouponInputShown: PropTypes.bool,
  paymentTypes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      tab: PropTypes.number,
      showIcon: PropTypes.bool,
      iconPath: PropTypes.string
    })
  ),
  planTypes: PropTypes.arrayOf(
    PropTypes.shape({
      planCode: PropTypes.string,
      priceMonth: PropTypes.number,
      priceTotal: PropTypes.priceTotal,
      numberOfMonths: PropTypes.string,
      showDiscount: PropTypes.bool
    })
  ),
  recurlyPublicKey: PropTypes.string,
  selectedPlan: PropTypes.shape({
    planCode: PropTypes.string,
    PriceMonth: PropTypes.number,
    priceTotal: PropTypes.number,
    numberOfMonths: PropTypes.string,
    showDiscount: PropTypes.bool
  }),
  verifiedCoupon: PropTypes.shape({
    discountPercentage: PropTypes.number
  })
};

export default PaymentForm;
